import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useNavigate, useParams } from 'react-router-dom';
import { BASELINE } from '../../util';

const LessonPage = () => {
    const navigate = useNavigate();
    const { cid } = useParams(); // URL에서 course id 받아오기 (예: /dev/class/1/lesson -> cid: "1")
    const courseId = parseInt(cid, 10);

    // 서버에서 lesson 데이터를 불러오기 위한 state
    const [lessons, setLessons] = useState([]);

    // Add Modal 상태 및 입력 필드 (새 lesson 추가)
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newLessonTitle, setNewLessonTitle] = useState('');
    // 이제 quiz 타입 선택 시에도 나머지 입력폼과 동일하게 처리 (추가 필드 제거)
    const [newLessonType, setNewLessonType] = useState('html');
    const [newLessonContent, setNewLessonContent] = useState('');
    const [newLessonStatus, setNewLessonStatus] = useState('open');

    // Edit Modal 상태 및 편집할 lesson 상태
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingLesson, setEditingLesson] = useState(null);
    const [editLessonTitle, setEditLessonTitle] = useState('');
    const [editLessonType, setEditLessonType] = useState('html');
    const [editLessonContent, setEditLessonContent] = useState('');
    const [editLessonStatus, setEditLessonStatus] = useState('open');

    // quiz 타입에서 사용 가능한 topic 옵션 (이전 버전에서는 사용했지만, 지금은 quiz 타입도 일반 HTML lesson처럼 처리)
    const quizTopics = [
        { value: 'algebra', label: 'Algebra', max: 10 },
        { value: 'triangle', label: 'Triangle', max: 20 },
    ];

    // 컴포넌트 마운트 시 서버에서 lesson 데이터를 GET (course_id 쿼리 사용)
    useEffect(() => {
        const fetchLessons = async () => {
            try {
                const response = await axios.get(`${BASELINE}api/lessons`, { params: { course_id: courseId } });
                if (response.data.success) {
                    setLessons(response.data.lessons);
                } else {
                    alert("Error fetching lessons: " + response.data.message);
                }
            } catch (error) {
                console.error("Error fetching lessons:", error);
                alert("Failed to fetch lessons. Please try again.");
            }
        };
        fetchLessons();
    }, [courseId]);

    // 드래그 앤 드롭 종료 핸들러: 순서 변경 후 서버 PUT 요청
    const onDragEnd = async (result) => {
        if (!result.destination) return;
        const reorderedLessons = Array.from(lessons);
        const [removed] = reorderedLessons.splice(result.source.index, 1);
        reorderedLessons.splice(result.destination.index, 0, removed);
        // 새로운 order_index 할당 (0부터 시작)
        reorderedLessons.forEach((item, index) => {
            item.order_index = index;
        });
        setLessons(reorderedLessons);
        // 새로운 순서 배열 생성
        const newOrder = reorderedLessons.map(lesson => lesson.id);
        try {
            const response = await axios.put(`${BASELINE}api/lessons/reorder`, { order: newOrder }, {
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.data.success) {
                // 성공 시 최신 데이터 다시 불러오기
                const refreshResponse = await axios.get(`${BASELINE}api/lessons`, { params: { course_id: courseId } });
                if (refreshResponse.data.success) {
                    setLessons(refreshResponse.data.lessons);
                }
            } else {
                alert("Error updating order: " + response.data.message);
            }
        } catch (error) {
            console.error("Error updating lesson order:", error);
            alert("Failed to update order. Please try again.");
        }
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        const newLessonData = {
            course_id: courseId,
            title: newLessonTitle,
            content: newLessonContent,
            order: 0, // 기본값, 서버에서 처리 가능
            status: newLessonStatus,
            type: newLessonType, // type만 quiz로 지정 가능
        };

        // quiz 타입 선택 시에도 추가 입력 필드를 사용하지 않고, 일반 lesson과 동일하게 처리합니다.
        newLessonData.topic = '';
        newLessonData.questions = null;

        try {
            const response = await axios.post(`${BASELINE}api/lessons`, newLessonData, {
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.data.success) {
                const addedLesson = { id: response.data.id, ...newLessonData };
                setLessons([...lessons, addedLesson]);
                // 폼 리셋 및 모달 닫기
                setNewLessonTitle('');
                setNewLessonType('html');
                setNewLessonContent('');
                setNewLessonStatus('open');
                setIsAddModalOpen(false);
            } else {
                alert("Error: " + response.data.message);
            }
        } catch (error) {
            console.error("Error adding lesson:", error);
            alert("Failed to add lesson. Please try again.");
        }
    };

    // Lesson 삭제 핸들러: DELETE 요청
    const handleLessonDelete = async (lessonId) => {
        if (window.confirm("Are you sure you want to remove this lesson?")) {
            try {
                const response = await axios.delete(`${BASELINE}api/lessons/${lessonId}`);
                if (response.data.success) {
                    setLessons(lessons.filter(lesson => lesson.id !== lessonId));
                } else {
                    alert("Error: " + response.data.message);
                }
            } catch (error) {
                console.error("Error deleting lesson:", error);
                alert("Failed to delete lesson. Please try again.");
            }
        }
    };

    // Lesson 수정 모달 열기: 편집할 lesson 데이터를 미리 채워줌
    const handleEditLessonClick = (lesson) => {
        setEditingLesson(lesson);
        setEditLessonTitle(lesson.title);
        setEditLessonType(lesson.type);
        setEditLessonContent(lesson.content);
        setEditLessonStatus(lesson.status);
        // quiz 타입에 대해 추가 필드는 제거
        setIsEditModalOpen(true);
    };

    // Lesson 수정 제출 핸들러: PUT 요청
    const handleEditLessonSubmit = async (e) => {
        e.preventDefault();
        const updatedLessonData = {
            course_id: courseId,
            title: editLessonTitle,
            content: editLessonContent,
            order: 0, // 기존 값 유지 또는 서버 처리
            status: editLessonStatus,
        };
        if (editLessonType === 'quiz') {
            // quiz 추가 필드 제거
            updatedLessonData.topic = '';
            updatedLessonData.questions = null;
        }
        try {
            const response = await axios.put(`${BASELINE}api/lessons/${editingLesson.id}`, updatedLessonData, {
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.data.success) {
                setLessons(lessons.map(lesson =>
                    lesson.id === editingLesson.id ? { ...lesson, ...updatedLessonData } : lesson
                ));
                setEditingLesson(null);
                setIsEditModalOpen(false);
            } else {
                alert("Error: " + response.data.message);
            }
        } catch (error) {
            console.error("Error updating lesson:", error);
            alert("Failed to update lesson. Please try again.");
        }
    };

    // 카드 배경색: status가 open이면 녹색, maintance이면 주황색
    const getBgColor = (status) => (status === 'open' ? 'bg-green-200' : 'bg-orange-200');

    return (
        <div className="p-6">
            {/* 네비게이션 버튼 */}
            <div className="flex space-x-2 mb-4">
                <button
                    onClick={() => navigate('/dev/class')}
                    className="px-4 py-2 bg-gray-300 rounded"
                >
                    Back
                </button>
                <button
                    onClick={() => navigate(`/dev/class/${courseId}/lesson/review`)}
                    className="px-4 py-2 bg-blue-300 rounded"
                >
                    Review Course
                </button>
            </div>

            <h1 className="text-2xl font-bold mb-4">Lessons</h1>

            {/* DragDropContext: 전체 lessons를 드래그 앤 드롭으로 정렬 */}
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="lessons">
                    {(provided) => (
                        <div className="grid grid-cols-3 gap-4" ref={provided.innerRef} {...provided.droppableProps}>
                            {/* + 카드: 새 lesson 추가 (드래그 대상 아님) */}
                            <div
                                className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:bg-gray-100"
                                onClick={() => setIsAddModalOpen(true)}
                            >
                                <span className="text-4xl text-gray-500">+</span>
                            </div>
                            {/* 기존 lesson 카드들 (content 제외, drag and drop 가능) */}
                            {lessons.map((lesson, index) => (
                                <Draggable key={lesson.id} draggableId={lesson.id.toString()} index={index}>
                                    {(provided) => (
                                        <div
                                            className={`border border-gray-300 rounded-lg p-4 ${getBgColor(lesson.status)}`}
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            onClick={() => navigate(`/dev/class/${courseId}/lesson/edit/${lesson.id}`)}
                                        >
                                            <h2 className="font-semibold">{lesson.title}</h2>
                                            <p className="text-sm text-gray-500">Type: {lesson.type}</p>
                                            {lesson.type === 'quiz' && (
                                                <>
                                                    <p className="text-sm text-gray-500">Topic: {lesson.topic}</p>
                                                    <p className="text-sm text-gray-500">Questions: {lesson.questions}</p>
                                                </>
                                            )}
                                            <p className="text-sm text-gray-500">Status: {lesson.status}</p>
                                            <div className="mt-2 flex space-x-2">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleEditLessonClick(lesson); }}
                                                    className="px-3 py-1 text-sm bg-green-500 text-white rounded"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleLessonDelete(lesson.id); }}
                                                    className="px-3 py-1 text-sm bg-red-500 text-white rounded"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            {/* Add New Lesson Modal */}
            {isAddModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
                    <div className="bg-white p-6 rounded-lg w-96">
                        <h2 className="text-xl font-bold mb-4">Add New Lesson</h2>
                        <form onSubmit={handleAddSubmit}>
                            <div className="mb-4">
                                <label className="block mb-1 font-medium">Title</label>
                                <input
                                    type="text"
                                    value={newLessonTitle}
                                    onChange={(e) => setNewLessonTitle(e.target.value)}
                                    className="w-full border border-gray-300 rounded px-3 py-2"
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block mb-1 font-medium">Type</label>
                                <select
                                    value={newLessonType}
                                    onChange={(e) => setNewLessonType(e.target.value)}
                                    className="w-full border border-gray-300 rounded px-3 py-2"
                                >
                                    <option value="html">HTML</option>
                                    <option value="markdown">Markdown</option>
                                    <option value="quiz">Quiz</option>
                                </select>
                            </div>
                            {/* quiz 추가 필드를 제거하여 모든 타입이 동일하게 처리 */}
                            <div className="mb-4">
                                <label className="block mb-1 font-medium">Content</label>
                                <textarea
                                    value={newLessonContent}
                                    onChange={(e) => setNewLessonContent(e.target.value)}
                                    className="w-full border border-gray-300 rounded px-3 py-2"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block mb-1 font-medium">Status</label>
                                <select
                                    value={newLessonStatus}
                                    onChange={(e) => setNewLessonStatus(e.target.value)}
                                    className="w-full border border-gray-300 rounded px-3 py-2"
                                >
                                    <option value="open">open</option>
                                    <option value="maintance">maintance</option>
                                </select>
                            </div>
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => setIsAddModalOpen(false)}
                                    className="mr-2 px-4 py-2 border rounded"
                                >
                                    Close
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-500 text-white rounded"
                                >
                                    Submit
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Lesson Modal */}
            {isEditModalOpen && editingLesson && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
                    <div className="bg-white p-6 rounded-lg w-96">
                        <h2 className="text-xl font-bold mb-4">Edit Lesson</h2>
                        <form onSubmit={handleEditLessonSubmit}>
                            <div className="mb-4">
                                <label className="block mb-1 font-medium">Title</label>
                                <input
                                    type="text"
                                    value={editLessonTitle}
                                    onChange={(e) => setEditLessonTitle(e.target.value)}
                                    className="w-full border border-gray-300 rounded px-3 py-2"
                                    required
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block mb-1 font-medium">Type</label>
                                <select
                                    value={editLessonType}
                                    onChange={(e) => setEditLessonType(e.target.value)}
                                    className="w-full border border-gray-300 rounded px-3 py-2"
                                >
                                    <option value="html">HTML</option>
                                    <option value="markdown">Markdown</option>
                                    <option value="quiz">Quiz</option>
                                </select>
                            </div>
                            {/* quiz 추가 필드 제거 */}
                            <div className="mb-4">
                                <label className="block mb-1 font-medium">Content</label>
                                <textarea
                                    value={editLessonContent}
                                    onChange={(e) => setEditLessonContent(e.target.value)}
                                    className="w-full border border-gray-300 rounded px-3 py-2"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block mb-1 font-medium">Status</label>
                                <select
                                    value={editLessonStatus}
                                    onChange={(e) => setEditLessonStatus(e.target.value)}
                                    className="w-full border border-gray-300 rounded px-3 py-2"
                                >
                                    <option value="open">open</option>
                                    <option value="maintance">maintance</option>
                                </select>
                            </div>
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="mr-2 px-4 py-2 border rounded"
                                >
                                    Close
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-500 text-white rounded"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LessonPage;
